import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import { articlesFor } from "@/data/insightsMap";
import { formatInsightDate, readTimeLabel } from "@/data/insights";
import { track } from "@/lib/analytics";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Related Insights — published articles relevant to this page.
 * Renders only articles that actually exist (registry-guarded).
 */
export default function RelatedInsights({ testid, slugs, badge = "Related Insights", headline = "Perspectives on this problem." }) {
  const articles = articlesFor(slugs);
  if (!articles.length) return null;
  return (
    <section data-testid={testid} className="py-16 md:py-20 bg-white border-t border-black/5">
      <div className={GRID}>
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <SectionBadge label={badge} />
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-6 leading-[1.1]">
              {headline}
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid={`${testid}-all`}
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-black hover:underline underline-offset-4 shrink-0"
          >
            All Insights <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className={`grid gap-6 md:gap-8 ${articles.length === 1 ? "md:grid-cols-1 max-w-xl" : articles.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/insights/${a.slug}`}
              data-testid={`${testid}-card-${a.slug}`}
              onClick={() => track("related_insight_click", { article: a.slug, source_page: window.location.pathname })}
              className="group relative bg-white border border-zinc-200 flex flex-col transition-all duration-500 hover:border-black hover:-translate-y-1"
            >
              {a.image && (
                <div className="aspect-[16/9] overflow-hidden bg-zinc-200">
                  <img
                    src={a.image}
                    alt={a.imageAlt || a.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-7 flex flex-col flex-1">
                <div className="text-xs text-zinc-500">
                  {formatInsightDate(a.date)} · {readTimeLabel(a)}
                </div>
                <h3 className="mt-2.5 font-serif-display text-xl leading-snug text-black">
                  {a.title}
                </h3>
                <p className="mt-2.5 text-sm text-zinc-600 leading-relaxed flex-1">
                  {a.excerpt}
                </p>
                <div className="mt-5 flex items-center gap-2 text-sm font-medium text-black">
                  Read the perspective
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
